import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import stylesData from '../styles.json';
import { SEO } from '../components/SEO';

interface StyleImage {
  name: string;
  url: string;
  path: string;
}

interface Genre {
  name: string;
  styles: StyleImage[];
}

export const Styles = () => {
  const [genres, setGenres] = useState<Genre[]>([]);
  const [selectedGenre, setSelectedGenre] = useState<Genre | null>(null);
  const [loadingStyles, setLoadingStyles] = useState(true);

  useEffect(() => {
    const loadStyles = () => {
      const loadedGenres: Genre[] = stylesData.map(genre => ({
        name: genre.name,
        styles: genre.images.map(img => ({
          name: img.split('/').pop() || img,
          url: img,
          path: img
        }))
      }));

      // Sort genres alphabetically
      loadedGenres.sort((a, b) => a.name.localeCompare(b.name));
      
      setGenres(loadedGenres);
      if (loadedGenres.length > 0) {
        setSelectedGenre(loadedGenres[0]);
      }
      setLoadingStyles(false);
    };

    loadStyles();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO 
        title="Artistic Styles"
        description="Explore our curated collection of artistic styles. From classic impressionism to modern abstract, find the perfect aesthetic for your masterpiece."
        url="https://www.atelier-persopol.com/styles"
      />
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-serif font-bold text-gray-900 mb-4">Artistic Styles</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Explore our curated collection of artistic styles. From classic impressionism to modern abstract, 
            find the perfect aesthetic for your masterpiece.
          </p>
        </div>

        {loadingStyles ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin mb-4" />
            <p>Loading styles library...</p>
          </div>
        ) : (
          <div className="flex flex-col">
            {/* Genres Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-4 mb-8 justify-center scrollbar-hide">
              {genres.map(genre => (
                <button
                  key={genre.name}
                  onClick={() => setSelectedGenre(genre)}
                  className={`px-6 py-3 rounded-full whitespace-nowrap text-sm font-medium transition-all duration-200 ${
                    selectedGenre?.name === genre.name
                      ? 'bg-black text-white shadow-md transform scale-105'
                      : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'
                  }`}
                >
                  {genre.name}
                </button>
              ))}
            </div>

            {/* Styles Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {selectedGenre?.styles.map((style) => (
                <div 
                  key={style.name}
                  className="group relative bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={style.url} 
                      alt={style.name}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                    <p className="text-white font-medium truncate">{style.name.split('.')[0]}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {genres.length === 0 && !loadingStyles && (
                <div className="text-center py-12 text-gray-500">
                    No styles found. Please check back later.
                </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
